import { useMutation } from '@apollo/client/react'
import { Modal, notification } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  type ContainerDocumentQuery,
  type Customer,
  DisableCustomerDocument,
  SendContainerDocumentDocument,
  SetCustomerPaywallDocument,
  UnDeleteContainerDocumentDocument,
  UnShredContainerDocumentDocument,
} from '~app/graph/generated/graphql'

const useDisableCustomerModal = () => {
  const { t } = useTranslation()
  const [disableCustomerMutation, { loading: disableCustomerMutationLoading }] =
    useMutation(DisableCustomerDocument)

  return {
    showModal: ({
      encodedPublicId,
      firstName,
      lastName,
      planId,
      id,
    }: Customer) =>
      Modal.confirm({
        title: t('disableCustomer.modalTitle'),
        content: (
          <div>
            {t('disableCustomer.modalBody')}
            <br />
            <br />
            <h4>
              Public Id: {encodedPublicId} <br />
              {t('misc.firstName')}: {firstName} <br />
              {t('misc.lastName')}: {lastName} <br />
              Plan: {planId}?
            </h4>
          </div>
        ),
        okText: t('labels.yes'),
        cancelText: t('labels.no'),
        autoFocusButton: 'cancel',
        cancelButtonProps: { type: 'primary' },
        okButtonProps: {
          type: 'primary',
          danger: true,
          disabled: !!disableCustomerMutationLoading,
        },
        onOk: async () => {
          const result = await disableCustomerMutation({
            variables: { id },
          })
          if (!result) {
            return
          }
          const { data, error } = result
          if (error) {
            notification.error({
              placement: 'bottomRight',
              message: t('misc.error'),
              description: t('disableCustomer.errorText') + error.message,
            })
          }
          if (data) {
            notification.success({
              placement: 'bottomRight',
              message: t('misc.disabled'),
              description: t('disableCustomer.successText'),
            })
          }
        },
      }),
  }
}

const useEnablePaywallModal = () => {
  const { t } = useTranslation()
  const [
    setCustomerPaywallMutation,
    { loading: setCustomerPaywallMutationLoading },
  ] = useMutation(SetCustomerPaywallDocument)
  return {
    loading: setCustomerPaywallMutationLoading,
    showModal: ({
      encodedPublicId,
      firstName,
      lastName,
      planId,
      id,
    }: Customer) =>
      Modal.confirm({
        title: t('paywallCustomer.modalTitle'),
        content: (
          <div>
            {t('paywallCustomer.modalBody')}
            <br />
            <br />
            <h4>
              Public Id: {encodedPublicId} <br />
              {t('misc.firstName')}: {firstName} <br />
              {t('misc.lastName')}: {lastName} <br />
              Plan: {planId}?
            </h4>
          </div>
        ),
        okText: t('labels.yes'),
        cancelText: t('labels.no'),
        autoFocusButton: 'cancel',
        cancelButtonProps: { type: 'primary' },
        okButtonProps: {
          type: 'primary',
          danger: true,
          disabled: !!setCustomerPaywallMutationLoading,
        },
        onOk: async () => {
          const result = await setCustomerPaywallMutation({
            variables: { id, isPaywalled: true },
          })
          if (!result) {
            return
          }
          const { data, error } = result
          if (error) {
            notification.error({
              placement: 'bottomRight',
              message: t('misc.error'),
              description: t('paywallCustomer.errorText') + error.message,
            })
          }
          if (data) {
            notification.success({
              placement: 'bottomRight',
              message: t('paywallCustomer.successTitle'),
              description: t('paywallCustomer.successText'),
            })
          }
        },
      }),
  }
}
const useDisablePaywallModal = () => {
  const { t } = useTranslation()
  const [
    setCustomerPaywallMutation,
    { loading: setCustomerPaywallMutationLoading },
  ] = useMutation(SetCustomerPaywallDocument)
  return {
    loading: setCustomerPaywallMutationLoading,
    showModal: ({
      encodedPublicId,
      firstName,
      lastName,
      planId,
      id,
    }: Customer) =>
      Modal.confirm({
        title: t('unPaywallCustomer.modalTitle'),
        content: (
          <div>
            {t('unPaywallCustomer.modalBody')}
            <br />
            <br />
            <h4>
              Public Id: {encodedPublicId} <br />
              {t('misc.firstName')}: {firstName} <br />
              {t('misc.lastName')}: {lastName} <br />
              Plan: {planId}?
            </h4>
          </div>
        ),
        okText: t('labels.yes'),
        cancelText: t('labels.no'),
        autoFocusButton: 'cancel',
        cancelButtonProps: { type: 'primary' },
        okButtonProps: {
          type: 'primary',
          danger: true,
          disabled: !!setCustomerPaywallMutationLoading,
        },
        onOk: async () => {
          const result = await setCustomerPaywallMutation({
            variables: { id, isPaywalled: false },
          })
          if (!result) {
            return
          }
          const { data, error } = result
          if (error) {
            notification.error({
              placement: 'bottomRight',
              message: t('misc.error'),
              description: t('unPaywallCustomer.errorText') + error.message,
            })
          }
          if (data) {
            notification.success({
              placement: 'bottomRight',
              message: t('unPaywallCustomer.successTitle'),
              description: t('unPaywallCustomer.successText'),
            })
          }
        },
      }),
  }
}

const useCustomerAccountActions = () => {
  const { showModal: showDisableCustomerModal } = useDisableCustomerModal()
  const {
    showModal: showEnableCustomerPaywallModal,
    loading: EnableCustomerPaywallLoading,
  } = useEnablePaywallModal()
  const { showModal: showDisablePaywallModal, loading: DisablePaywallLoading } =
    useDisablePaywallModal()

  return {
    showDisableCustomerModal,
    showEnableCustomerPaywallModal,
    showDisablePaywallModal,
    paywallMutationLoading:
      EnableCustomerPaywallLoading || DisablePaywallLoading,
  }
}

export default useCustomerAccountActions

type CustomerDocumentAction = Pick<
  ContainerDocumentQuery['containerDocument'],
  'isDestroyed' | 'currentLocationType' | 'id' | 'canReturn'
> & {
  owner: Pick<ContainerDocumentQuery['containerDocument']['owner'], 'id'>
}

export const useCustomerDocumentAction = (
  containerDocument: CustomerDocumentAction | undefined
) => {
  const [unShredContainerDocument] = useMutation(
    UnShredContainerDocumentDocument
  )
  const [unDeleteContainerDocument] = useMutation(
    UnDeleteContainerDocumentDocument
  )
  const [sendContainerDocument] = useMutation(SendContainerDocumentDocument)

  return {
    onCustomerAccountAction: async () =>
      !containerDocument
        ? () => {
            throw new Error('No document found')
          }
        : containerDocument.isDestroyed
          ? unDeleteContainerDocument({
              variables: {
                customerId: containerDocument.owner.id,
                containerId: containerDocument.id,
              },
            })
          : containerDocument.currentLocationType === 'ShredBasket'
            ? unShredContainerDocument({
                variables: {
                  customerId: containerDocument.owner.id,
                  containerId: containerDocument.id,
                },
              })
            : containerDocument.canReturn &&
              sendContainerDocument({
                variables: {
                  customerId: containerDocument.owner.id,
                  containerId: containerDocument.id,
                },
              }),
  }
}
